import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Gtag } from 'angular-gtag';
import { MessageService } from 'primeng/api';
import { Observable, withLatestFrom } from 'rxjs';
import { Notification } from 'src/app/core/models';
import { EnvService } from 'src/app/core/services';
import {
  RespondToNotification,
  SetCgm90Enrollment,
  UpdateNotifications,
} from 'src/app/core/store/notifications/notifications.actions';
import { NotificationsState } from 'src/app/core/store/notifications/notifications.state';
import { ApiStoreData } from 'src/app/core/store/utils';
import { asFormControl } from 'src/app/core/utils/common';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent implements OnInit {
  @Input() notification!: Notification;
  @Select(NotificationsState.respondToNotificationState) respondToNotificationState$!: Observable<ApiStoreData<null>>;
  @Select(NotificationsState.setCGM90EnrollmentState) setCgm90EnrollmentState$!: Observable<ApiStoreData<null>>;

  cgm90EnrollmentActionId?: String = this.envService.notifications.cgm90EnrollmentActionId;
  displayCgm90NotificationModal: boolean = true;

  characterLimit = 200;
  characterCount = 0;

  asFormControl = asFormControl;

  responseForm = new FormGroup({
    response: new FormControl('', [Validators.required, Validators.maxLength(this.characterLimit)]),
  });

  constructor(
    private store: Store,
    private messageService: MessageService,
    private gtag: Gtag,
    private envService: EnvService
  ) {}

  ngOnInit() {
    this.gtag.event('show_notification', {
      event_category: 'engagement',
    });

    this.responseForm.valueChanges.subscribe((value) => {
      this.characterCount = value.response.length;
    });
  }

  responseToNotification(notificationId: number, responseId: number) {
    this.store
      .dispatch(new RespondToNotification(notificationId, responseId))
      .pipe(withLatestFrom(this.respondToNotificationState$))
      .subscribe(() => {
        this.store.dispatch(new UpdateNotifications());
      });
  }

  responseToCgm90Enrollment(isPrimaryBtn: boolean, notificationId: number, consented: boolean) {
    this.store
      .dispatch(new SetCgm90Enrollment(notificationId, consented))
      .pipe(withLatestFrom(this.setCgm90EnrollmentState$))
      .subscribe(([_, respondToCgm90Enrollment]) => {
        this.hideCgm90NotificationModal();

        if (isPrimaryBtn) {
          this.gtag.event('enroll_in_cgm90', {
            event_category: 'engagement',
          });

          if (respondToCgm90Enrollment.isFinished && !respondToCgm90Enrollment.error) {
            this.messageService.add({
              key: 'homeToast',
              severity: 'success',
              detail: 'Successfully enrolled in CGM 90-day program!',
            });

            this.store.dispatch(new UpdateNotifications());
          } else {
            this.messageService.add({
              key: 'homeToast',
              severity: 'error',
              detail: 'Could not enroll in CGM 90-day program at this time.',
            });
          }
        } else {
          this.gtag.event('reject_cgm90_enrollment', {
            event_category: 'engagement',
          });

          this.store.dispatch(new UpdateNotifications());
        }
      });
  }

  showCgm90NotificationModal() {
    this.displayCgm90NotificationModal = true;
  }

  hideCgm90NotificationModal() {
    this.displayCgm90NotificationModal = false;
  }

  saveNotificationResponse(notificationId: number) {
    if (this.responseForm.invalid) {
      this.responseForm.markAllAsTouched();
      return;
    }

    this.store
      .dispatch(new RespondToNotification(notificationId, 1, this.responseForm.value.response))
      .pipe(withLatestFrom(this.respondToNotificationState$))
      .subscribe(() => {
        this.store.dispatch(new UpdateNotifications());
      });
  }

  clearNotificationResponse() {
    this.responseForm.reset();
    this.characterCount = 0;
  }
}
